import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import departmentData from './departmentData';
import { colors, typography, shadows, transitions } from '../styles/StyleGuide';
import { Helmet } from 'react-helmet-async';
import { SITE_URL } from '../config/site';
import NotFound from './NotFound';

const SHEET_URL = 'https://opensheet.elk.sh/1tyUMWAbAXvhq-ELglsa4MXXCBbMDVx385QtXe1SKhY0/Sheet1';
const DEPARTMENT_CONTENT_SHEET_URL = 'https://opensheet.elk.sh/1ock0QiDwK9fOB6kAF0HliIDFbAWlOrWWzQiZRUKuqo4/sheet1';

// Simple in-memory cache
const dataCache = {
  data: null,
  timestamp: 0,
  CACHE_DURATION: 5 * 60 * 1000, // 5 minutes
};

const departmentContentCache = {
  data: null,
  timestamp: 0,
  CACHE_DURATION: 5 * 60 * 1000, // 5 minutes
};

// Map of URL param → sheet "Department" values
const departmentDivisionMap = {
  anatomy: ['ANATOMY'],
  anaesthesiology: ['ANAESTHESIOLOGY'],
  biochemistry: ['Biochemistry'],
  'community-medicine': ['Community Medicine'],
  dentistry: ['DENTISTRY'],
  dermatology: ['Dermatology '],
  dvl: ['Dermatology '],
  'emergency-medicine': ['EMERGENCY MEDICINE'],
  ent: ['ENT'],
  'forensic-medicine': ['Forensic Medicine and Toxicology'],
  'general-medicine': ['GENERAL MEDICINE'],
  'general-surgery': ['General Surgery'],
  'hospital-administration': ['Hospital Administration'],
  'mch-neuro-surgery': ['M.Ch - Neuro Surgery'],
  'mch-surgical-oncology': ['Surgical oncology'],
  ophthalmology: ['Ophthalmology', 'OPHTHALMOLOGY'],
  microbiology: ['MICROBIOLOGY'],
  neurology: ['Neurology'],
  cardiology: ['Cardiology'],
  'obstetrics-gynaecology': ['OBSTETRICS & GYNAECOLOGY'],
  orthopedics: ['ORTHOPAEDICS'],
  paediatrics: ['Paediatrics'],
  pathology: ['PATHOLOGY'],
  pharmacology: ['PHARMACOLOGY'],
  physiology: ['Physiology'],
  psychiatry: ['PSYCHIATRY'],
  'pulmonary-medicine': ['PULMONOLOGY', 'Pulmonology'],
  'radio-diagnosis': ['Radiology'],
  'radiation-oncology': ['Radiation oncology'],
  'transfusion-medicine': ['Transfusion Medicine'],
  urology: ['M.Ch - Urology'],
};

const departmentContentMap = {
  anatomy: ['Anatomy'],
  anaesthesiology: ['Anesthesiology', 'Anaesthesiology'],
  biochemistry: ['Biochemistry'],
  cardiology: ['DM - Cardiology', 'Cardiology'],
  'community-medicine': ['Community Medicine/ SPM', 'Community Medicine'],
  dentistry: ['Dentistry'],
  dermatology: ['Dermatology'],
  dvl: ['Dermatology'],
  'emergency-medicine': ['Emergency Medicine'],
  ent: ['ENT'],
  'forensic-medicine': ['Forensic Medicine'],
  'general-medicine': ['General Medicine'],
  'general-surgery': ['General Surgery'],
  'hospital-administration': ['Hospital Administration'],
  'mch-neuro-surgery': ['M.Ch - Neuro Surgery'],
  'mch-surgical-oncology': ['M.Ch - Surgical Oncology'],
  microbiology: ['Microbiology'],
  neurology: ['DM - Neurology', 'Neurology'],
  'obstetrics-gynaecology': ['Obstetrics & Gynaecology'],
  ophthalmology: ['Ophthalmology'],
  orthopedics: ['Orthopaedics'],
  paediatrics: ['Paediatrics'],
  pathology: ['Pathology'],
  pharmacology: ['Pharmacology'],
  physiology: ['Physiology'],
  psychiatry: ['Psychiatry'],
  'pulmonary-medicine': ['Respiratory medicine', 'Pulmonology'],
  'radio-diagnosis': ['Radiology'],
  'radiation-oncology': ['Radiation Oncology'],
  'transfusion-medicine': ['Transfusion Medicine'],
  urology: ['M.Ch - Urology'],
};

const sectionTitles = new Set([
  'department profile / overview',
  'ug & pg teaching activities',
  'research projects & publications',
  'conferences / cme / workshops',
  'clinical services & special clinics (where applicable)',
  'field activities / outreach programs',
  'awards & recognitions',
  'infrastructure / facilities available in the department',
  'any other department-specific contributions',
  'any other department-specific contributions,',
]);

const normalizeText = (value) => String(value || '').replace(/\s+/g, ' ').trim();
const normalizeMatch = (value) => normalizeText(value).toLowerCase();
const normalizeDepartmentName = (value) =>
  normalizeMatch(String(value || '').replace(/[()/,&.-]+/g, ' '));

const departmentsMatch = (sheetDepartment, mappedDepartment) => {
  const sheetValue = normalizeDepartmentName(sheetDepartment);
  const mappedValue = normalizeDepartmentName(mappedDepartment);

  if (!sheetValue || !mappedValue) {
    return false;
  }

  return (
    sheetValue === mappedValue ||
    sheetValue.startsWith(`${mappedValue} `) ||
    mappedValue.startsWith(`${sheetValue} `)
  );
};

const parseResearchGroups = (content) => {
  let text = String(content || '')
    .replace(/\r/g, '\n')
    .replace(/\n+/g, ' ')
    .replace(/\s*[•●○]\s*/g, ' @@ITEM@@ ')
    .replace(/\s*(\d+)\s*\.\s*(?=Dr\.?\s*[A-Z])/g, '\n##DOCTOR##$1. ')
    .replace(/\s*(\d+)\s*,\s*(?=Dr\.?\s*[A-Z])/g, '\n##DOCTOR##$1. ')
    .replace(/\s+(?=Dr\.?\s*[A-Z][^,.]{1,80},?\s*(?:@@ITEM@@|[12]\.|[A-Z]))/g, '\n')
    .replace(/@@ITEM@@/g, '\n@@ITEM@@ ')
    .replace(/\s{2,}/g, ' ')
    .trim();

  const doctorChunks = text
    .split('\n')
    .map((chunk) => chunk.trim())
    .filter(Boolean)
    .reduce((acc, line) => {
      if (/^(##DOCTOR##)?\d+\.\s*Dr\.?/i.test(line) || /^Dr\.?/i.test(line)) {
        acc.push(line.replace(/^##DOCTOR##/, ''));
      } else if (acc.length > 0) {
        acc[acc.length - 1] = `${acc[acc.length - 1]} ${line}`.trim();
      } else {
        acc.push(line);
      }
      return acc;
    }, []);

  return doctorChunks.map((chunk) => {
    const normalized = chunk.replace(/^##DOCTOR##/, '').trim();
    const bulletSplit = normalized
      .replace(/\s*@@ITEM@@\s*/g, '\n@@ITEM@@ ')
      .split('\n')
      .map((part) => part.trim())
      .filter(Boolean);

    const heading = bulletSplit.shift() || normalized;
    const publications = [];

    bulletSplit.forEach((part) => {
      if (part.startsWith('@@ITEM@@')) {
        publications.push(part.replace(/^@@ITEM@@\s*/, '').trim());
      } else if (publications.length > 0) {
        publications[publications.length - 1] = `${publications[publications.length - 1]} ${part}`.trim();
      } else {
        const splitPubs = part
          .replace(/\s+(?=\d+\.\s)/g, '\n')
          .split('\n')
          .map((item) => item.trim())
          .filter(Boolean);
        if (splitPubs.length > 1) {
          publications.push(...splitPubs);
        } else if (part) {
          publications.push(part);
        }
      }
    });

    const cleanHeading = heading
      .replace(/\s*@@ITEM@@.*$/, '')
      .replace(/\s+/g, ' ')
      .trim();

    return {
      heading: cleanHeading,
      publications: publications
        .map((item) => item.replace(/\s+/g, ' ').trim())
        .filter(Boolean),
    };
  }).filter((group) => group.heading);
};

const formatSectionContent = (title, content) => {
  const normalizedTitle = normalizeMatch(title);
  let text = String(content || '')
    .replace(/\r/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  if (!text) {
    return [];
  }

  if (normalizedTitle === 'research projects & publications') {
    return parseResearchGroups(text);
  }

  if (/awards|conferences|clinical services|field activities|infrastructure|contributions|teaching activities/.test(normalizedTitle)) {
    text = text
      .replace(/(?:^|\s)(\d{1,2}\.)\s+(?=[A-Z])/g, '\n$1 ')
      .replace(/\s*[•●○·]\s*/g, '\n- ')
      .replace(/\n{2,}/g, '\n')
      .trim();

    const items = text
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean);

    if (items.length > 1) {
      return items;
    }
  }

  return text
    .split('\n\n')
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);
};

const renderSectionBody = (title, content) => {
  const blocks = formatSectionContent(title, content);
  const normalizedTitle = normalizeMatch(title);

  if (blocks.length === 0) {
    return (
      <p style={{
        color: colors.textSecondary,
        lineHeight: typography.lineHeights.relaxed,
        fontSize: typography.fontSizes.md,
        textAlign: 'left',
        marginBottom: '0',
      }}>
        Nil
      </p>
    );
  }

  if (normalizedTitle === 'research projects & publications') {
    return (
      <div>
        {blocks.map((group, index) => (
          <div key={`${title}-${index}`} style={{ marginBottom: index === blocks.length - 1 ? '0' : '1.5rem' }}>
            <h6 style={{
              color: colors.primary,
              fontWeight: typography.fontWeights.semiBold,
              marginBottom: '0.75rem',
              fontSize: typography.fontSizes.md,
              textAlign: 'left',
            }}>
              {group.heading}
            </h6>
            {group.publications.length > 0 ? (
              <ul style={{
                paddingLeft: '1.5rem',
                marginBottom: '0',
                listStyleType: 'disc',
                textAlign: 'left',
              }}>
                {group.publications.map((item, itemIndex) => (
                  <li
                    key={`${title}-${index}-${itemIndex}`}
                    style={{
                      marginBottom: '0.6rem',
                      lineHeight: typography.lineHeights.relaxed,
                      color: colors.textSecondary,
                      fontSize: typography.fontSizes.md,
                      textAlign: 'left',
                    }}
                  >
                    {item}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ))}
      </div>
    );
  }

  if (blocks.length === 1) {
    return (
      <p style={{
        color: colors.textSecondary,
        lineHeight: typography.lineHeights.relaxed,
        fontSize: typography.fontSizes.md,
        textAlign: 'left',
        marginBottom: '0',
        whiteSpace: 'pre-line',
      }}>
        {blocks[0]}
      </p>
    );
  }

  return (
    <ul style={{
      paddingLeft: '1.5rem',
      marginBottom: '0',
      listStyleType: 'disc',
      textAlign: 'left',
    }}>
      {blocks.map((block, index) => (
        <li
          key={`${title}-${index}`}
          style={{
            marginBottom: '0.75rem',
            lineHeight: typography.lineHeights.relaxed,
            color: colors.textSecondary,
            fontSize: typography.fontSizes.md,
            textAlign: 'left',
            whiteSpace: 'pre-line',
          }}
        >
          {block}
        </li>
      ))}
    </ul>
  );
};

const getDepartmentContentCandidates = (departmentId, dept) => {
  const explicit = departmentContentMap[departmentId] || [];
  const fallbackName = dept?.title?.replace(/^Department of\s*/i, '').trim();
  return [...explicit, fallbackName, departmentId.replace(/-/g, ' ')].filter(Boolean);
};

const parseDepartmentContent = (rows) => {
  const groups = [];
  let currentDepartment = null;

  rows.forEach((row) => {
    const normalizedRow = {};
    Object.entries(row).forEach(([key, value]) => {
      normalizedRow[normalizeText(key)] = typeof value === 'string' ? value.trim() : value;
    });

    const title = normalizeText(normalizedRow['']);
    const content = Object.entries(normalizedRow)
      .filter(([key, value]) => key !== '' && normalizeText(value))
      .map(([, value]) => String(value).trim())
      .join('\n\n');

    if (!title && !content) {
      return;
    }

    const normalizedTitle = normalizeMatch(title.replace(/,+$/, ''));
    const isSectionTitle = sectionTitles.has(normalizedTitle);

    if (!isSectionTitle && title && !content) {
      currentDepartment = {
        name: title,
        normalizedName: normalizeMatch(title),
        sections: [],
      };
      groups.push(currentDepartment);
      return;
    }

    if (!currentDepartment) {
      return;
    }

    if (isSectionTitle) {
      currentDepartment.sections.push({
        title: title.replace(/,+$/, ''),
        content,
      });
      return;
    }

    if (!title && content && currentDepartment.sections.length > 0) {
      const lastSection = currentDepartment.sections[currentDepartment.sections.length - 1];
      lastSection.content = lastSection.content ? `${lastSection.content}\n${content}` : content;
    }
  });

  return groups;
};

const findDepartmentContentSections = (groups, departmentId, dept) => {
  const candidates = getDepartmentContentCandidates(departmentId, dept).map(normalizeMatch);
  const matchedGroup = groups.find((group) =>
    candidates.some((candidate) =>
      group.normalizedName === candidate ||
      group.normalizedName.includes(candidate) ||
      candidate.includes(group.normalizedName)
    )
  );

  return matchedGroup ? matchedGroup.sections : [];
};

const LoadingSpinner = () => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem', color: colors.primary }}>
    <div style={{
      width: 50, height: 50,
      border: `3px solid ${colors.primaryLight}`,
      borderTop: `3px solid ${colors.primary}`,
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      marginBottom: '1rem'
    }} />
    <style>
      {`
        @keyframes spin { to { transform: rotate(360deg); } }
        .staff-table { opacity: 0; transform: translateY(20px); animation: fadeIn 0.5s ease forwards; }
        @keyframes fadeIn { to { opacity: 1; transform: translateY(0); } }
        .department-card { background: ${colors.bgLight}; border-radius: 12px; padding: 2rem; box-shadow: ${shadows.sm}; transition: ${transitions.default}; }
        .department-card:hover { box-shadow: ${shadows.md}; }
        .staff-table th { background: ${colors.primary}; color: ${colors.textLight}; font-weight: ${typography.fontWeights.medium}; }
        .staff-table tr:hover { background-color: ${colors.primaryLight}; transition: ${transitions.fast}; }
      `}
    </style>
    <p style={{ fontSize: typography.fontSizes.md, fontWeight: typography.fontWeights.medium }}>
      Loading staff data...
    </p>
  </div>
);

export default function DepartmentTemplate() {
  const { departmentId } = useParams();
  const dept = departmentData[departmentId];
  const [staff, setStaff] = useState([]);
  const [contentSections, setContentSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,  setError]  = useState(null);

  // Normalize each row’s keys (trim & collapse whitespace/newlines)
  const normalizeRow = row => {
    const out = {};
    Object.entries(row).forEach(([key, val]) => {
      const cleanKey = key.trim().replace(/\s+/g, ' ');
      out[cleanKey] = typeof val === 'string' ? val.trim() : val;
    });
    return out;
  };

  useEffect(() => {
    async function fetchStaff() {
      setLoading(true); setError(null);
      try {
        let data;
        const now = Date.now();

        // Use cache if fresh
        if (dataCache.data && (now - dataCache.timestamp < dataCache.CACHE_DURATION)) {
          data = dataCache.data;
        } else {
          const res = await fetch(SHEET_URL);
          if (!res.ok) throw new Error(`Status ${res.status}`);
          data = await res.json();
          dataCache.data = data;
          dataCache.timestamp = now;
        }

        // Normalize all rows
        const normalized = data.map(normalizeRow);

        // Determine which divisions to filter by
        let divisions = departmentDivisionMap[departmentId];
        if (!divisions || !divisions.length) {
          const fallbackName = dept?.title?.replace(/^Department of\s*/i, '').trim();
          divisions = fallbackName ? [fallbackName] : [departmentId.replace(/-/g, ' ')];
        }
        divisions = divisions.map(d => d.trim().toUpperCase());

        // Filter out any blank rows & match by Department field
        const filtered = normalized.filter(row => {
          if (!row.Department) return false;
          return divisions.some(div => departmentsMatch(row.Department, div));
        });

        // Sort by Total teaching experience descending (robustly parses "XX YEARS YY MONTHS")
        const parseExperienceMonths = (value) => {
          if (value == null) return 0;
          const raw = String(value).toUpperCase();
          // Try to extract explicit years/months markers (handles "2YEARS 9 MONTHS" too)
          const yearsMatch = raw.match(/(\d+(?:\.\d+)?)\s*Y/);
          const monthsMatch = raw.match(/(\d+(?:\.\d+)?)\s*M/);
          if (yearsMatch || monthsMatch) {
            const years = yearsMatch ? parseFloat(yearsMatch[1]) : 0;
            const months = monthsMatch ? parseFloat(monthsMatch[1]) : 0;
            return Math.round(years * 12 + months);
          }
          // Fallback: plain number -> treat as years
          const numeric = parseFloat(raw.replace(/[^0-9.]/g, ''));
          return Number.isFinite(numeric) ? Math.round(numeric * 12) : 0;
        };

        filtered.sort((a, b) => {
          const expA = parseExperienceMonths(a['Total teaching exp(in yrs)']);
          const expB = parseExperienceMonths(b['Total teaching exp(in yrs)']);
          if (expA !== expB) return expB - expA; // Descending by months
          // Tie-breaker: alphabetical by name
          const nameA = (a['Faculty name'] || '').toString().toLowerCase();
          const nameB = (b['Faculty name'] || '').toString().toLowerCase();
          return nameA.localeCompare(nameB);
        });

        setStaff(filtered);
      } catch (err) {
        setError(err.message);
        setStaff([]);
      } finally {
        setLoading(false);
      }
    }

    if (dept) {
      fetchStaff();
    } else {
      setLoading(false);
      setStaff([]);
    }
  }, [departmentId, dept]);

  useEffect(() => {
    async function fetchDepartmentContent() {
      try {
        let data;
        const now = Date.now();

        if (
          departmentContentCache.data &&
          (now - departmentContentCache.timestamp < departmentContentCache.CACHE_DURATION)
        ) {
          data = departmentContentCache.data;
        } else {
          const res = await fetch(DEPARTMENT_CONTENT_SHEET_URL);
          if (!res.ok) throw new Error(`Status ${res.status}`);
          data = await res.json();
          departmentContentCache.data = data;
          departmentContentCache.timestamp = now;
        }

        const groups = parseDepartmentContent(data);
        setContentSections(findDepartmentContentSections(groups, departmentId, dept));
      } catch (err) {
        setContentSections([]);
      }
    }

    if (dept) {
      fetchDepartmentContent();
    } else {
      setContentSections([]);
    }
  }, [departmentId, dept]);

  if (!dept) {
    return <NotFound title="Department Not Found" message="The requested department does not exist." backTo="/academics" />;
  }

  const overviewSection = contentSections.find(
    (section) => normalizeMatch(section.title) === 'department profile / overview'
  );
  const additionalContentSections = contentSections.filter(
    (section) => normalizeMatch(section.title) !== 'department profile / overview'
  );
  const aboutDescription = overviewSection?.content || dept.description;
  const aboutTitle = overviewSection?.title || 'About the Department';

  return (
    <div className="container py-5">
      <Helmet>
        <title>{`${dept.title} | ACSR GMC Nellore`}</title>
        <meta name="description" content={(dept.description || '').slice(0, 160)} />
        <link rel="canonical" href={`${SITE_URL}/departments/${departmentId}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={`${dept.title} | ACSR GMC Nellore`} />
        <meta property="og:description" content={(dept.description || '').slice(0, 200)} />
        <meta property="og:url" content={`${SITE_URL}/departments/${departmentId}`} />
      </Helmet>
      <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
        <h2 style={{
          color: colors.primary,
          fontSize: typography.fontSizes.xl,
          fontWeight: typography.fontWeights.bold,
          marginBottom: '1.5rem',
          textAlign: 'center'
        }}>{dept.title}</h2>

        {/* Department Staff Table - Show First */}
        {loading ? (
          <LoadingSpinner />
        ) : error ? (
          <div className="alert alert-danger" style={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            padding: '1rem',
            borderRadius: '8px',
            textAlign: 'center',
            marginBottom: '2rem'
          }}>
            <p className="mb-0">Error loading data: {error}</p>
          </div>
        ) : staff.length === 0 ? (
          <NotFound title="No Content" message="No staff data available for this department." backTo="/academics" />
        ) : (
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{
              color: colors.primary,
              fontSize: typography.fontSizes.lg,
              fontWeight: typography.fontWeights.semiBold,
              marginBottom: '1rem',
              borderBottom: `2px solid ${colors.primary}`,
              paddingBottom: '0.5rem'
            }}>Department Staff</h3>
            <div className="table-responsive staff-table">
              <table className="table table-bordered table-hover">
                <thead>
                  <tr>
                    <th>Faculty Name</th>
                    <th>Designation</th>
                    <th>Professional Qualifications</th>
                    <th>Medical Registration No</th>
                    <th>AEBAS Attend. ID</th>
                    <th>Nature of Employment</th>
                    <th>Total Teaching Experience</th>
                  </tr>
                </thead>
                <tbody>
                  {staff.map((row, idx) => (
                    <tr key={`${row['Faculty name'] || idx}-${row.Designation || ''}`}>
                      <td>{row['Faculty name']}</td>
                      <td>{row.Designation}</td>
                      <td>{row['Professional Qualifications']}</td>
                      <td>{row['Medical Registration No']}</td>
                      <td>{row['AEBAS Attend. ID']}</td>
                      <td>{row['Nature of employment']}</td>
                      <td>{row['Total teaching exp(in yrs)']}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Department Description */}
        <div style={{ marginBottom: '2rem' }}>
          <h3 style={{
            color: colors.primary,
            fontSize: typography.fontSizes.lg,
            fontWeight: typography.fontWeights.semiBold,
            marginBottom: '1rem',
            borderBottom: `2px solid ${colors.primary}`,
            paddingBottom: '0.5rem'
          }}>{aboutTitle}</h3>
          <div style={{
            backgroundColor: colors.bgLight,
            padding: '1.5rem',
            borderRadius: '8px',
            boxShadow: shadows.sm
          }}>
            <p style={{
              color: colors.textSecondary,
              lineHeight: typography.lineHeights.relaxed,
              fontSize: typography.fontSizes.md,
              textAlign: 'left',
              marginBottom: '0',
              whiteSpace: 'pre-line'
            }}>{aboutDescription}</p>
          </div>
        </div>

        {/* HOD Details Section */}
        {dept.hodDetails && (
          <div style={{ marginBottom: '2rem' }}>
            <h5 style={{
              color: colors.primary,
              fontSize: typography.fontSizes.lg,
              fontWeight: typography.fontWeights.semiBold,
              marginBottom: '1rem',
              borderBottom: `2px solid ${colors.primary}`,
              paddingBottom: '0.5rem'
            }}>{dept.hodDetails.title}</h5>
            
            <div style={{
              backgroundColor: colors.bgLight,
              padding: '1.5rem',
              borderRadius: '8px',
              boxShadow: shadows.sm
            }}>
              <h6 style={{
                color: colors.primary,
                fontWeight: typography.fontWeights.medium,
                marginBottom: '0.75rem',
                fontSize: typography.fontSizes.md,
                textAlign: 'left'
              }}>Qualifications:</h6>
              <ul style={{ 
                marginBottom: '1.5rem', 
                paddingLeft: '1.5rem',
                listStyleType: 'disc',
                textAlign: 'left'
              }}>
                {dept.hodDetails.qualifications.map((qual, index) => (
                  <li key={index} style={{ 
                    marginBottom: '0.5rem',
                    lineHeight: typography.lineHeights.relaxed,
                    color: colors.textSecondary,
                    textAlign: 'left'
                  }}>{qual}</li>
                ))}
              </ul>

              {dept.hodDetails.positions && (
                <>
                  <h6 style={{
                    color: colors.primary,
                    fontWeight: typography.fontWeights.medium,
                    marginBottom: '0.75rem',
                    fontSize: typography.fontSizes.md
                  }}>Positions:</h6>
                  <ul style={{ 
                    marginBottom: '1.5rem', 
                    paddingLeft: '1.5rem',
                    listStyleType: 'disc',
                    textAlign: 'left'
                  }}>
                    {dept.hodDetails.positions.map((position, index) => (
                      <li key={index} style={{ 
                        marginBottom: '0.5rem',
                        lineHeight: typography.lineHeights.relaxed,
                        color: colors.textSecondary,
                        textAlign: 'left'
                      }}>{position}</li>
                    ))}
                  </ul>
                </>
              )}

              {dept.hodDetails.awards && (
                <>
                  <h6 style={{
                    color: colors.primary,
                    fontWeight: typography.fontWeights.medium,
                    marginBottom: '0.75rem',
                    fontSize: typography.fontSizes.md
                  }}>Awards:</h6>
                  <ul style={{ 
                    marginBottom: '1.5rem', 
                    paddingLeft: '1.5rem',
                    listStyleType: 'disc',
                    textAlign: 'left'
                  }}>
                    {dept.hodDetails.awards.map((award, index) => (
                      <li key={index} style={{ 
                        marginBottom: '0.5rem',
                        lineHeight: typography.lineHeights.relaxed,
                        color: colors.textSecondary,
                        textAlign: 'left'
                      }}>{award}</li>
                    ))}
                  </ul>
                </>
              )}

              {dept.hodDetails.workshops && (
                <>
                  <h6 style={{
                    color: colors.primary,
                    fontWeight: typography.fontWeights.medium,
                    marginBottom: '0.75rem',
                    fontSize: typography.fontSizes.md
                  }}>Workshops & Committees:</h6>
                  <ul style={{ 
                    paddingLeft: '1.5rem',
                    listStyleType: 'disc',
                    textAlign: 'left'
                  }}>
                    {dept.hodDetails.workshops.map((workshop, index) => (
                      <li key={index} style={{ 
                        marginBottom: '0.5rem',
                        lineHeight: typography.lineHeights.relaxed,
                        color: colors.textSecondary,
                        textAlign: 'left'
                      }}>{workshop}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        )}

        {/* Activities Section */}
        {dept.activities && (
          <div style={{ marginBottom: '2rem' }}>
            <h5 style={{
              color: colors.primary,
              fontSize: typography.fontSizes.lg,
              fontWeight: typography.fontWeights.semiBold,
              marginBottom: '1rem',
              borderBottom: `2px solid ${colors.primary}`,
              paddingBottom: '0.5rem'
            }}>ACTIVITIES CARRIED OUT BY THE DEPARTMENT</h5>
            <div style={{
              backgroundColor: colors.bgLight,
              padding: '1.5rem',
              borderRadius: '8px',
              boxShadow: shadows.sm
            }}>
              <ul style={{ 
                paddingLeft: '1.5rem',
                marginBottom: '0',
                listStyleType: 'disc',
                textAlign: 'left'
              }}>
                {dept.activities.map((activity, index) => (
                  <li key={index} style={{ 
                    marginBottom: '1rem',
                    lineHeight: typography.lineHeights.relaxed,
                    color: colors.textSecondary,
                    fontSize: typography.fontSizes.md,
                    textAlign: 'left'
                  }}>{activity}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {additionalContentSections.map((section, index) => (
          <div key={`${section.title}-${index}`} style={{ marginBottom: '2rem' }}>
            <h5 style={{
              color: colors.primary,
              fontSize: typography.fontSizes.lg,
              fontWeight: typography.fontWeights.semiBold,
              marginBottom: '1rem',
              borderBottom: `2px solid ${colors.primary}`,
              paddingBottom: '0.5rem'
            }}>{section.title}</h5>
            <div style={{
              backgroundColor: colors.bgLight,
              padding: '1.5rem',
              borderRadius: '8px',
              boxShadow: shadows.sm
            }}>
              {renderSectionBody(section.title, section.content)}
            </div>
          </div>
        ))}

        {/* Rural Health Centre Section */}
        {dept.ruralHealthCentre && (
          <div style={{ marginBottom: '2rem' }}>
            <h5 style={{
              color: colors.primary,
              fontSize: typography.fontSizes.lg,
              fontWeight: typography.fontWeights.semiBold,
              marginBottom: '1rem',
              borderBottom: `2px solid ${colors.primary}`,
              paddingBottom: '0.5rem'
            }}>{dept.ruralHealthCentre.title}</h5>
            <p style={{
              color: colors.textSecondary,
              fontSize: typography.fontSizes.md,
              lineHeight: typography.lineHeights.relaxed,
              marginBottom: '1rem',
              textAlign: 'left'
            }}>{dept.ruralHealthCentre.description}</p>
            <div style={{
              backgroundColor: colors.bgLight,
              padding: '1.5rem',
              borderRadius: '8px',
              boxShadow: shadows.sm
            }}>
              <h6 style={{
                color: colors.primary,
                fontWeight: typography.fontWeights.medium,
                marginBottom: '0.75rem',
                fontSize: typography.fontSizes.md,
                textAlign: 'left'
              }}>The various activities at RHTC are:</h6>
              <ul style={{ 
                paddingLeft: '1.5rem',
                marginBottom: '0',
                listStyleType: 'disc',
                textAlign: 'left'
              }}>
                {dept.ruralHealthCentre.activities.map((activity, index) => (
                  <li key={index} style={{ 
                    marginBottom: '1rem',
                    lineHeight: typography.lineHeights.relaxed,
                    color: colors.textSecondary,
                    fontSize: typography.fontSizes.md,
                    textAlign: 'left'
                  }}>{activity}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Urban Health Centre Section */}
        {dept.urbanHealthCentre && (
          <div style={{ marginBottom: '2rem' }}>
            <h5 style={{
              color: colors.primary,
              fontSize: typography.fontSizes.lg,
              fontWeight: typography.fontWeights.semiBold,
              marginBottom: '1rem',
              borderBottom: `2px solid ${colors.primary}`,
              paddingBottom: '0.5rem'
            }}>{dept.urbanHealthCentre.title}</h5>
            <p style={{
              color: colors.textSecondary,
              fontSize: typography.fontSizes.md,
              lineHeight: typography.lineHeights.relaxed,
              marginBottom: '1rem',
              textAlign: 'left'
            }}>{dept.urbanHealthCentre.description}</p>
            <div style={{
              backgroundColor: colors.bgLight,
              padding: '1.5rem',
              borderRadius: '8px',
              boxShadow: shadows.sm
            }}>
              <h6 style={{
                color: colors.primary,
                fontWeight: typography.fontWeights.medium,
                marginBottom: '0.75rem',
                fontSize: typography.fontSizes.md,
                textAlign: 'left'
              }}>The various activities at Urban Health Centre are:</h6>
              <ul style={{ 
                paddingLeft: '1.5rem',
                marginBottom: '0',
                listStyleType: 'disc',
                textAlign: 'left'
              }}>
                {dept.urbanHealthCentre.activities.map((activity, index) => (
                  <li key={index} style={{ 
                    marginBottom: '1rem',
                    lineHeight: typography.lineHeights.relaxed,
                    color: colors.textSecondary,
                    fontSize: typography.fontSizes.md,
                    textAlign: 'left'
                  }}>{activity}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Field Visits Section */}
        {dept.fieldVisits && (
          <div style={{ marginBottom: '2rem' }}>
            <h5 style={{
              color: colors.primary,
              fontSize: typography.fontSizes.lg,
              fontWeight: typography.fontWeights.semiBold,
              marginBottom: '1rem',
              borderBottom: `2px solid ${colors.primary}`,
              paddingBottom: '0.5rem'
            }}>FIELD VISITS FOR UNDER GRADUATE STUDENTS</h5>
            <div style={{
              backgroundColor: colors.bgLight,
              padding: '1.5rem',
              borderRadius: '8px',
              boxShadow: shadows.sm
            }}>
              <ul style={{ 
                paddingLeft: '1.5rem',
                marginBottom: '0',
                listStyleType: 'disc',
                textAlign: 'left'
              }}>
                {dept.fieldVisits.map((visit, index) => (
                  <li key={index} style={{ 
                    marginBottom: '1rem',
                    lineHeight: typography.lineHeights.relaxed,
                    color: colors.textSecondary,
                    fontSize: typography.fontSizes.md,
                    textAlign: 'left'
                  }}>{visit}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Publications Section */}
        {dept.publications && (
          <div style={{ marginBottom: '2rem' }}>
            <h5 style={{
              color: colors.primary,
              fontSize: typography.fontSizes.lg,
              fontWeight: typography.fontWeights.semiBold,
              marginBottom: '1rem',
              borderBottom: `2px solid ${colors.primary}`,
              paddingBottom: '0.5rem'
            }}>Publications:</h5>
            <div style={{
              backgroundColor: colors.bgLight,
              padding: '1.5rem',
              borderRadius: '8px',
              boxShadow: shadows.sm
            }}>
              {Object.entries(dept.publications).map(([author, publications]) => (
                <div key={author} style={{ marginBottom: '1.5rem' }}>
                  <h6 style={{
                    color: colors.primary,
                    fontWeight: typography.fontWeights.medium,
                    marginBottom: '0.75rem',
                    fontSize: typography.fontSizes.md,
                    textAlign: 'left'
                  }}>{author}</h6>
                  <ul style={{ 
                    paddingLeft: '1.5rem',
                    marginBottom: '0',
                    listStyleType: 'disc',
                    textAlign: 'left'
                  }}>
                    {publications.map((pub, index) => (
                      <li key={index} style={{ 
                        marginBottom: '0.75rem',
                        lineHeight: typography.lineHeights.relaxed,
                        color: colors.textSecondary,
                        fontSize: typography.fontSizes.md,
                        textAlign: 'left'
                      }}>{pub}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Ongoing Research Projects Section */}
        {dept.ongoingResearchProjects && (
          <div style={{ marginBottom: '2rem' }}>
            <h5 style={{
              color: colors.primary,
              fontSize: typography.fontSizes.lg,
              fontWeight: typography.fontWeights.semiBold,
              marginBottom: '1rem',
              borderBottom: `2px solid ${colors.primary}`,
              paddingBottom: '0.5rem'
            }}>Ongoing Research Projects:</h5>
            <div style={{
              backgroundColor: colors.bgLight,
              padding: '1.5rem',
              borderRadius: '8px',
              boxShadow: shadows.sm
            }}>
              <ul style={{ 
                paddingLeft: '1.5rem',
                marginBottom: '0',
                listStyleType: 'disc',
                textAlign: 'left'
              }}>
                {dept.ongoingResearchProjects.map((project, index) => (
                  <li key={index} style={{ 
                    marginBottom: '1rem',
                    lineHeight: typography.lineHeights.relaxed,
                    color: colors.textSecondary,
                    fontSize: typography.fontSizes.md,
                    textAlign: 'left'
                  }}>{project}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Outreach Activities Section */}
        {dept.outreachActivities && (
          <div style={{ marginBottom: '2rem' }}>
            <h5 style={{
              color: colors.primary,
              fontSize: typography.fontSizes.lg,
              fontWeight: typography.fontWeights.semiBold,
              marginBottom: '1rem',
              borderBottom: `2px solid ${colors.primary}`,
              paddingBottom: '0.5rem'
            }}>Outreach Activities by ACSR GMC, NELLORE:</h5>
            <div style={{
              backgroundColor: colors.bgLight,
              padding: '1.5rem',
              borderRadius: '8px',
              boxShadow: shadows.sm
            }}>
              <ul style={{ 
                paddingLeft: '1.5rem',
                marginBottom: '0',
                listStyleType: 'disc',
                textAlign: 'left'
              }}>
                {dept.outreachActivities.map((activity, index) => (
                  <li key={index} style={{ 
                    marginBottom: '1rem',
                    lineHeight: typography.lineHeights.relaxed,
                    color: colors.textSecondary,
                    fontSize: typography.fontSizes.md,
                    textAlign: 'left'
                  }}>{activity}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Workshops/CME Section */}
        {dept.workshopsCME && (
          <div style={{ marginBottom: '2rem' }}>
            <h5 style={{
              color: colors.primary,
              fontSize: typography.fontSizes.lg,
              fontWeight: typography.fontWeights.semiBold,
              marginBottom: '1rem',
              borderBottom: `2px solid ${colors.primary}`,
              paddingBottom: '0.5rem'
            }}>Workshops / CME / National Days:</h5>
            <div style={{
              backgroundColor: colors.bgLight,
              padding: '1.5rem',
              borderRadius: '8px',
              boxShadow: shadows.sm
            }}>
              <ul style={{ 
                paddingLeft: '1.5rem',
                marginBottom: '0',
                listStyleType: 'disc',
                textAlign: 'left'
              }}>
                {dept.workshopsCME.map((workshop, index) => (
                  <li key={index} style={{ 
                    marginBottom: '1rem',
                    lineHeight: typography.lineHeights.relaxed,
                    color: colors.textSecondary,
                    fontSize: typography.fontSizes.md,
                    textAlign: 'left'
                  }}>{workshop}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
